'use client';

import {
  type ComponentPropsWithoutRef,
  type FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import clsx from 'clsx';
import { useInterval } from 'react-use';

import { Loader } from '@/components/momentum/components/loader';

import styles from './iframe-embed.module.css';

export interface IFrameEmbedProps
  extends Omit<ComponentPropsWithoutRef<'iframe'>, 'onLoad' | 'width'> {
  height?: number;
  width?: number;
}

export const IFrameEmbed: FC<IFrameEmbedProps> = ({
  height,
  width,
  ...props
}: IFrameEmbedProps) => {
  const ref = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(height ?? 200);
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (ref.current && props.src) {
      ref.current.src = props.src;
    }
  }, [props.src]);

  useInterval(() => {
    if (!height && ref.current?.contentWindow) {
      const { contentWindow } = ref.current;
      setIframeHeight(contentWindow.document.body?.scrollHeight);
    }
  }, 200);

  const onLoad = useCallback(() => {
    if (ref.current) {
      setLoaded(true);
    }
  }, []);

  return (
    <div
      className={clsx({
        [styles['embed']]: true,
        [styles['embed--loaded']]: loaded,
      })}
    >
      <div className={styles['embed__container']}>
        <iframe
          {...props}
          className={clsx(styles['embed__iframe'], props.className)}
          title={props.title ?? 'embedded content'}
          ref={ref}
          onLoad={onLoad}
          width={width ? `${width}px` : '100%'}
          height={`${iframeHeight}px`}
          allow={props.allow ?? ''}
          allowFullScreen={props.allow?.includes('fullscreen') ?? false}
        />
      </div>
      {!loaded && (
        <div className={styles['embed__loader']}>
          <Loader />
        </div>
      )}
    </div>
  );
};
